import React, { useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "~/store/hooks";
import { useIntl } from "@ai360/core/intl";
import { SteeringDataAPI } from "@ai360/core";

import { Checkbox, NoLink, Loader, LoaderTypes, DialogBox, DialogBoxFooterType } from "~/core";

import { steeringDataActions, steeringDataSelectors } from "./data";
import { getEmbeddingStatusLabel, getStatusClassName } from "./status";
import { messages } from "./i18n-messages";
import "./steering-data-child-list.css";

type ISteeringDataRecord = SteeringDataAPI.ISteeringDataRecord;

interface ISteeringDataChildListProps {
    parentGuid: string;
    /** Parent source type — decides file- vs page-oriented labels. */
    sourceType?: ISteeringDataRecord["sourceType"];
}

// Sublist of a container parent's per-child records. For a url-files parent or a
// manual upload the children are files; for a url-text parent they are the linked
// pages whose text was captured — labels adapt accordingly.
// Lets the user see each child and delete one or many. Fetches its children on
// mount and reads them from the shared childrenByParent store slice.
export const SteeringDataChildList = ({ parentGuid, sourceType }: ISteeringDataChildListProps) => {
    const dispatch = useAppDispatch();
    const { formatMessage } = useIntl();

    // url-text children are captured web pages (links), not files; everything else
    // (url-files, manual upload) is files.
    const isPages = sourceType === "url-text";
    const labels = {
        count: isPages ? messages.pagesCount : messages.filesCount,
        empty: isPages ? messages.pagesEmpty : messages.filesEmpty,
        column: isPages ? messages.childColumnPage : messages.childColumnFile,
        deleteConfirm: isPages
            ? messages.pageDeleteConfirmationMessage
            : messages.childDeleteConfirmationMessage,
    };

    const { children, loading } = useAppSelector((state) =>
        steeringDataSelectors.getSteeringDataChildrenState(state, parentGuid)
    );

    const [selectedGuids, setSelectedGuids] = useState<Set<string>>(new Set());
    const [confirmDelete, setConfirmDelete] = useState<string[] | null>(null);

    useEffect(() => {
        dispatch(steeringDataActions.fetchSteeringDataChildren(parentGuid));
    }, [dispatch, parentGuid]);

    // Drop any selected guids that no longer exist (e.g. after a delete).
    useEffect(() => {
        setSelectedGuids((prev) => {
            const present = new Set(children.map((c) => c.steeringDataGuid));
            const next = new Set([...prev].filter((g) => present.has(g)));
            return next.size === prev.size ? prev : next;
        });
    }, [children]);

    const allSelected = children.length > 0 && selectedGuids.size === children.length;

    const toggleOne = (guid: string) => {
        setSelectedGuids((prev) => {
            const next = new Set(prev);
            if (next.has(guid)) {
                next.delete(guid);
            } else {
                next.add(guid);
            }
            return next;
        });
    };

    const toggleAll = () => {
        setSelectedGuids((prev) =>
            prev.size === children.length
                ? new Set()
                : new Set(children.map((c) => c.steeringDataGuid))
        );
    };

    const requestDeleteSelected = () => {
        if (selectedGuids.size === 0) return;
        setConfirmDelete([...selectedGuids]);
    };

    const requestDeleteOne = (guid: string) => {
        setConfirmDelete([guid]);
    };

    const onConfirmDelete = () => {
        if (!confirmDelete || confirmDelete.length === 0) return;
        if (confirmDelete.length === 1) {
            // Single file (per-row Delete): keep the one-shot endpoint.
            dispatch(
                steeringDataActions.deleteSteeringDataChild({
                    parentGuid,
                    childGuid: confirmDelete[0],
                })
            );
        } else {
            // Multiple files (Delete Selected): one bulk call instead of N calls.
            dispatch(
                steeringDataActions.deleteSteeringDataChildren({
                    parentGuid,
                    childGuids: confirmDelete,
                })
            );
        }
        setConfirmDelete(null);
    };

    const rows = useMemo(
        () =>
            children.map((child: ISteeringDataRecord) => {
                const statusLabel = getEmbeddingStatusLabel(child.embeddingStatus);
                return {
                    guid: child.steeringDataGuid,
                    fileName: child.fileName || child.name || "",
                    statusLabel,
                };
            }),
        [children]
    );

    return (
        <div className="steering-data-child-list">
            {loading && children.length === 0 ? (
                <div className="steering-data-child-list__loading">
                    <Loader type={LoaderTypes.LINE_SCALE} />
                </div>
            ) : rows.length === 0 ? (
                <div className="steering-data-child-list__empty">{formatMessage(labels.empty)}</div>
            ) : (
                <>
                    <div className="steering-data-child-list__toolbar">
                        <span className="steering-data-child-list__count">
                            {formatMessage(labels.count, { count: rows.length })}
                        </span>
                        <NoLink
                            className="steering-data-child-list__delete-selected"
                            disabled={selectedGuids.size === 0}
                            onClick={requestDeleteSelected}
                            label={formatMessage(messages.childDeleteSelected)}
                        />
                    </div>
                    <div className="steering-data-child-list__scroll">
                        <table className="steering-data-child-list__table">
                            <thead>
                                <tr>
                                    <th className="steering-data-child-list__col-check">
                                        <Checkbox value={allSelected} onChange={toggleAll} />
                                    </th>
                                    <th className="steering-data-child-list__col-file">
                                        {formatMessage(labels.column)}
                                    </th>
                                    <th className="steering-data-child-list__col-status">
                                        {formatMessage(messages.childColumnStatus)}
                                    </th>
                                    <th className="steering-data-child-list__col-actions" />
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row) => (
                                    <tr key={row.guid}>
                                        <td className="steering-data-child-list__col-check">
                                            <Checkbox
                                                value={selectedGuids.has(row.guid)}
                                                onChange={() => toggleOne(row.guid)}
                                            />
                                        </td>
                                        <td
                                            className="steering-data-child-list__col-file"
                                            title={row.fileName}
                                        >
                                            {row.fileName}
                                        </td>
                                        <td className="steering-data-child-list__col-status">
                                            <span className={getStatusClassName(row.statusLabel)}>
                                                {row.statusLabel}
                                            </span>
                                        </td>
                                        <td className="steering-data-child-list__col-actions">
                                            <NoLink
                                                onClick={() => requestDeleteOne(row.guid)}
                                                label={formatMessage(messages.childDelete)}
                                                className="steering-data-child-list__row-delete"
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            <DialogBox
                action="Delete"
                draggable
                className="delete-confirmation-modal"
                footerType={DialogBoxFooterType.ACTION_CANCEL}
                isOpen={confirmDelete !== null}
                onAction={onConfirmDelete}
                onClose={() => setConfirmDelete(null)}
                title={formatMessage(messages.childDeleteConfirmationTitle)}
            >
                <p>
                    {confirmDelete
                        ? formatMessage(labels.deleteConfirm, {
                              count: confirmDelete.length,
                          })
                        : ""}
                </p>
            </DialogBox>
        </div>
    );
};

export default SteeringDataChildList;
